import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Send, ChevronRight, Paperclip, X } from 'lucide-react';
import { useApi } from '../auth/useApi';
import { useImagePaste, makeFetchUploader } from '../hooks/useImagePaste';
import { uploadTicketFile } from '../components/TicketAttachments';
import TechStackRequest from './TechStackRequest';

const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
};

interface FormDef {
  FormId: number;
  Name: string;
  Icon: string | null;
  Description: string | null;
  IsDefault: boolean;
  AutoAssignUserId: number | null;
  AutoCategoryId: number | null;
  AutoPriority: 'Low' | 'Medium' | 'High' | 'Critical' | null;
}

interface FormField {
  FieldId: number;
  FormId: number;
  Label: string;
  FieldType: 'text' | 'textarea' | 'date' | 'select' | 'multiselect' | 'checkbox';
  Required: boolean;
  OptionsJson: string | null;
  SortOrder: number;
}

interface Category { CategoryId: number; Name: string; }

interface Me {
  userId: number;
  email: string;
  displayName: string;
  role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent';
}

interface UserRow {
  UserId: number;
  Email: string;
  DisplayName: string;
  Active: boolean;
}

const optionsToList = (json: string | null): string[] => {
  if (!json) return [];
  try { const v = JSON.parse(json); return Array.isArray(v) ? v : []; }
  catch { return []; }
};

export const NewTicket: React.FC<{ me: Me }> = ({ me }) => {
  const api = useApi();
  const navigate = useNavigate();

  const [forms, setForms] = useState<FormDef[]>([]);
  const [fields, setFields] = useState<FormField[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  // Inline image paste → uploads via /api/kb/upload-image (reuses useApi's token + base URL).
  const descRef = useRef<HTMLTextAreaElement>(null);
  const uploadImage = useMemo(
    () => makeFetchUploader(api.apiBase, api.getAccessToken),
    [api.apiBase, api.getAccessToken],
  );
  const { onPaste: onDescPaste, uploading: imgUploading, error: imgError } = useImagePaste({
    value: description, onChange: setDescription, uploadImage, textareaRef: descRef,
  });
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [priority, setPriority] = useState<'Low' | 'Medium' | 'High' | 'Critical'>('Medium');
  const [customFields, setCustomFields] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  // Files staged before the ticket exists; uploaded right after creation.
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  // Tech Stack Request opens an embedded Typeform inline. No ticket is created
  // here -- the reviewing team emails the helpdesk inbox, which opens the ticket.
  const [showTechStack, setShowTechStack] = useState(false);

  // Requester picker — tech/admin can open a ticket on behalf of another user.
  // Defaults to the caller themselves.
  const canPickRequester = me.role === 'admin' || me.role === 'tech';
  const [allUsers, setAllUsers] = useState<UserRow[]>([]);
  const [requesterUserId, setRequesterUserId] = useState<number>(me.userId);

  useEffect(() => {
    (async () => {
      try {
        const [formsResp, catResp, usersResp] = await Promise.all([
          api.get<{ forms: FormDef[]; fields: FormField[] }>('/api/admin/forms'),
          api.get<Category[]>('/api/categories').catch(() => [] as Category[]),
          canPickRequester
            ? api.get<UserRow[]>('/api/users').catch(() => [] as UserRow[])
            : Promise.resolve([] as UserRow[]),
        ]);
        setForms(formsResp.forms ?? []);
        setFields(formsResp.fields ?? []);
        setCategories(Array.isArray(catResp) ? catResp : []);
        setAllUsers(Array.isArray(usersResp) ? usersResp.filter(u => u.Active !== false) : []);
        // Always land on the form picker so every option (including Tech Stack
        // Request) is visible, even when there's only one intake form.
      } catch (e: any) { setError(e?.message ?? 'Failed to load forms'); }
      finally { setLoading(false); }
    })();
  // eslint-disable-next-line
  }, []);

  const selectedForm = useMemo(
    () => forms.find(f => f.FormId === selectedFormId) ?? null,
    [forms, selectedFormId]
  );

  const formFields = useMemo(
    () => selectedFormId
      ? fields.filter(f => f.FormId === selectedFormId).sort((a, b) => a.SortOrder - b.SortOrder)
      : [],
    [fields, selectedFormId]
  );

  // Apply form auto-defaults when a form is selected
  useEffect(() => {
    if (!selectedForm) return;
    if (selectedForm.AutoCategoryId) setCategoryId(selectedForm.AutoCategoryId);
    if (selectedForm.AutoPriority) setPriority(selectedForm.AutoPriority);
  }, [selectedForm]);

  const updateCustom = (label: string, value: any) => {
    setCustomFields(prev => ({ ...prev, [label]: value }));
  };

  const validate = (): string | null => {
    if (!subject.trim()) return 'Subject is required';
    if (!description.trim()) return 'Description is required';
    for (const f of formFields) {
      if (f.Required) {
        const v = customFields[f.Label];
        if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
          return `"${f.Label}" is required`;
        }
      }
    }
    return null;
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) { alert(validationError); return; }

    setSubmitting(true);
    try {
      const body: any = {
        subject: subject.trim(),
        description: description.trim(),
        priority,
        formId: selectedFormId,
        formData: Object.keys(customFields).length > 0 ? customFields : undefined,
      };
      if (categoryId !== null) body.categoryId = categoryId;
      // Only send createdByUserId if the picker actually changed it
      if (canPickRequester && requesterUserId !== me.userId) {
        body.createdByUserId = requesterUserId;
      }

      const result = await api.post<{ ticketId: number; ticketKey: string }>('/api/tickets', body);

      // Upload any staged files now that we have a ticket id. A file failure
      // shouldn't lose the ticket — warn but still navigate to it.
      if (pendingFiles.length > 0) {
        try {
          for (const f of pendingFiles) {
            await uploadTicketFile(api.apiBase, api.getAccessToken, result.ticketId, f, false);
          }
        } catch (e: any) {
          alert(`Ticket ${result.ticketKey} was created, but a file upload failed: ${e?.message ?? 'unknown error'}. You can re-add it on the ticket.`);
        }
      }

      navigate(`/tickets/${result.ticketId}`);
    } catch (e: any) {
      alert(`Failed to create ticket: ${e?.message ?? 'unknown error'}`);
    } finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ padding: 40, color: C.textMuted }}>Loading…</div>;
  if (error) return (
    <div style={{ padding: 40, color: C.danger }}>
      <Link to="/tickets" style={backLinkSty}><ArrowLeft size={14} /> Back</Link>
      <div>Failed to load: {error}</div>
    </div>
  );

  // -------- Tech Stack Request (embedded external form) --------
  if (showTechStack) {
    return <TechStackRequest onBack={() => setShowTechStack(false)} />;
  }

  // -------- Form picker --------
  if (!selectedFormId) {
    return (
      <div>
        <Link to="/tickets" style={backLinkSty}><ArrowLeft size={14} /> Back to tickets</Link>
        <h1 style={{ color: C.primary, fontSize: 26, margin: '0 0 6px' }}>New Ticket</h1>
        <p style={{ color: C.textMuted, margin: '0 0 24px' }}>Pick a form to start.</p>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12,
        }}>
          {forms.map(f => (
            <button
              key={f.FormId}
              onClick={() => setSelectedFormId(f.FormId)}
              style={{
                background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
                padding: 16, textAlign: 'left', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 12,
                transition: 'border-color 0.15s, transform 0.1s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; }}
            >
              <div style={{ fontSize: 32 }}>{f.Icon ?? '📝'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: C.text }}>{f.Name}</div>
                {f.Description && (
                  <div style={{
                    fontSize: 12, color: C.textMuted, marginTop: 4,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>{f.Description}</div>
                )}
              </div>
              <ChevronRight size={16} color={C.textMuted} />
            </button>
          ))}

          {/* Tech Stack Request -- opens an embedded external form (Typeform) */}
          <button
            onClick={() => setShowTechStack(true)}
            style={{
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
              padding: 16, textAlign: 'left', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 12,
              transition: 'border-color 0.15s, transform 0.1s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; }}
          >
            <div style={{ fontSize: 32 }}>🧩</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 15, color: C.text }}>Tech Stack Request</div>
              <div style={{
                fontSize: 12, color: C.textMuted, marginTop: 4,
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              }}>Request new software or tools. Opens a short form to fill out.</div>
            </div>
            <ChevronRight size={16} color={C.textMuted} />
          </button>
        </div>
      </div>
    );
  }

  // -------- Render the picked form --------
  return (
    <div>
      <button
        onClick={() => { setSelectedFormId(null); setCustomFields({}); }}
        style={{
          ...backLinkSty,
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        }}
      >
        <ArrowLeft size={14} /> Choose a different form
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ fontSize: 36 }}>{selectedForm?.Icon ?? '📝'}</div>
        <div>
          <h1 style={{ color: C.primary, fontSize: 24, margin: 0 }}>{selectedForm?.Name}</h1>
          {selectedForm?.Description && (
            <p style={{ color: C.textMuted, margin: '4px 0 0', fontSize: 13 }}>{selectedForm.Description}</p>
          )}
        </div>
      </div>

      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
        padding: 24, maxWidth: 760,
      }}>
        {/* Requester picker — only visible to admin/tech */}
        {canPickRequester && (
          <Field label="Requester (on behalf of)">
            <select
              value={requesterUserId}
              onChange={e => setRequesterUserId(parseInt(e.target.value))}
              style={inputSty()}
            >
              <option value={me.userId}>{me.displayName} (me)</option>
              {allUsers
                .filter(u => u.UserId !== me.userId)
                .sort((a, b) => a.DisplayName.localeCompare(b.DisplayName))
                .map(u => (
                  <option key={u.UserId} value={u.UserId}>
                    {u.DisplayName} — {u.Email}
                  </option>
                ))}
            </select>
            {requesterUserId !== me.userId && (
              <div style={{ fontSize: 11, color: C.warn, marginTop: 4 }}>
                This ticket will be filed under the selected user. They'll receive the email notifications.
              </div>
            )}
          </Field>
        )}

        <Field label="Subject" required>
          <input value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="One-line summary"
            style={inputSty()} />
        </Field>

        <Field label="Description" required>
          <textarea ref={descRef} value={description} onChange={e => setDescription(e.target.value)}
            onPaste={onDescPaste}
            rows={5} placeholder="What's going on? Steps you've tried, error messages, anything that helps. You can paste images directly."
            style={{ ...inputSty(), resize: 'vertical', fontFamily: 'inherit' }} />
          {imgUploading && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Uploading image…</div>}
          {imgError && <div style={{ fontSize: 12, color: C.danger, marginTop: 4 }}>{imgError}</div>}
        </Field>

        {/* Category — hidden when form auto-assigns one */}
        {!selectedForm?.AutoCategoryId && categories.length > 0 && (
          <Field label="Category">
            <select value={categoryId ?? ''}
              onChange={e => setCategoryId(e.target.value ? parseInt(e.target.value) : null)}
              style={inputSty()}>
              <option value="">— uncategorized —</option>
              {categories.map(c => <option key={c.CategoryId} value={c.CategoryId}>{c.Name}</option>)}
            </select>
          </Field>
        )}

        {/* Priority — hidden when form auto-assigns one */}
        {!selectedForm?.AutoPriority && (
          <Field label="Priority">
            <div style={{ display: 'flex', gap: 6 }}>
              {(['Critical', 'High', 'Medium', 'Low'] as const).map(p => (
                <button key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  style={{
                    background: priority === p ? PRIORITY_COLORS[p] : C.bg,
                    color: priority === p ? '#fff' : C.text,
                    border: `1px solid ${priority === p ? PRIORITY_COLORS[p] : C.border}`,
                    padding: '6px 14px', borderRadius: 16, fontSize: 12,
                    cursor: 'pointer', fontWeight: 500,
                  }}
                >{p}</button>
              ))}
            </div>
          </Field>
        )}

        {/* Custom fields */}
        {formFields.length > 0 && (
          <div style={{
            marginTop: 8, paddingTop: 16, borderTop: `1px solid ${C.border}`,
          }}>
            {formFields.map(f => (
              <DynamicField key={f.FieldId}
                field={f}
                value={customFields[f.Label]}
                onChange={(v) => updateCustom(f.Label, v)} />
            ))}
          </div>
        )}

        {/* Attachments — staged now, uploaded after the ticket is created */}
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>
            Attachments <span style={{ color: C.textMuted, fontWeight: 400 }}>(optional · any file type · up to 25 MB each)</span>
          </label>
          <input
            ref={fileRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              if (e.target) e.target.value = '';
              const tooBig = picked.find(f => f.size > 25 * 1024 * 1024);
              if (tooBig) { alert(`"${tooBig.name}" is over the 25 MB limit.`); return; }
              setPendingFiles(prev => [...prev, ...picked]);
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, background: 'transparent',
              color: C.primary, border: `1px solid ${C.border}`, padding: '8px 14px',
              borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <Paperclip size={14} /> Add files
          </button>
          {pendingFiles.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
              {pendingFiles.map((f, i) => (
                <div key={`${f.name}-${i}`} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                  background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13,
                }}>
                  <Paperclip size={13} color={C.textMuted} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <span style={{ color: C.textMuted, fontSize: 12 }}>{(f.size / (1024 * 1024)).toFixed(1)} MB</span>
                  <button type="button" aria-label={`Remove ${f.name}`}
                    onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}
                    style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: C.danger }}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{
          marginTop: 24, paddingTop: 16, borderTop: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button
            onClick={() => navigate('/tickets')}
            style={{
              background: 'transparent', color: C.text, border: `1px solid ${C.border}`,
              padding: '8px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
            }}
          >Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            style={{
              background: submitting ? '#ccc' : C.primary, color: '#fff', border: 'none',
              padding: '8px 18px', borderRadius: 6, fontSize: 13, fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <Send size={12} /> {submitting ? 'Creating…' : 'Create Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Dynamic Field Renderer
// ============================================================================
const DynamicField: React.FC<{
  field: FormField;
  value: any;
  onChange: (v: any) => void;
}> = ({ field, value, onChange }) => {
  const options = optionsToList(field.OptionsJson);

  return (
    <Field label={field.Label} required={field.Required}>
      {field.FieldType === 'text' && (
        <input value={value ?? ''} onChange={e => onChange(e.target.value)} style={inputSty()} />
      )}
      {field.FieldType === 'textarea' && (
        <textarea value={value ?? ''} onChange={e => onChange(e.target.value)}
          rows={3} style={{ ...inputSty(), resize: 'vertical', fontFamily: 'inherit' }} />
      )}
      {field.FieldType === 'date' && (
        <input type="date" value={value ?? ''} onChange={e => onChange(e.target.value)} style={inputSty()} />
      )}
      {field.FieldType === 'select' && (
        <select value={value ?? ''} onChange={e => onChange(e.target.value)} style={inputSty()}>
          <option value="">— select —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
      {field.FieldType === 'multiselect' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {options.map(o => {
            const arr: string[] = Array.isArray(value) ? value : [];
            const checked = arr.includes(o);
            return (
              <label key={o} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 16,
                background: checked ? `${C.primary}20` : C.bg,
                border: `1px solid ${checked ? C.primary : C.border}`,
                cursor: 'pointer', fontSize: 12,
              }}>
                <input type="checkbox" checked={checked} style={{ margin: 0 }}
                  onChange={e => {
                    if (e.target.checked) onChange([...arr, o]);
                    else onChange(arr.filter(x => x !== o));
                  }} />
                {o}
              </label>
            );
          })}
        </div>
      )}
      {field.FieldType === 'checkbox' && (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
          <span style={{ fontSize: 13, color: C.textMuted }}>Yes / true</span>
        </label>
      )}
    </Field>
  );
};

// -------- Shared styles --------
const PRIORITY_COLORS: Record<string, string> = {
  Critical: '#c4533a',
  High: '#c4793a',
  Medium: '#7a9b7e',
  Low: '#a8a8a8',
};
const backLinkSty: React.CSSProperties = {
  color: C.primary, display: 'inline-flex', alignItems: 'center', gap: 4,
  marginBottom: 12, textDecoration: 'none', fontSize: 13,
};
const inputSty = (): React.CSSProperties => ({
  border: `1px solid ${C.border}`, padding: '8px 10px', borderRadius: 6,
  fontSize: 14, width: '100%', boxSizing: 'border-box', background: C.bg,
});
const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{
      fontSize: 11, fontWeight: 600, color: C.textMuted,
      marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>
      {label} {required && <span style={{ color: C.danger }}>*</span>}
    </div>
    {children}
  </div>
);
